import { useRef, useState } from "react";
import { Alert, Box, Button, Snackbar, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { nextStep, ONBOARDING_BASE_PATH } from "../registry";
import { useFlowState, useFlowActions } from "../../state/FlowProvider";
import OtpInput from "../../components/OtpInput";
import { onboardingTokens } from "../../theme";
import { resendEmailOtp, verifyEmailOtp } from "../../api";
import { t } from "../../../../locales/t";
import FormSubmitButton from "../../components/FormSubmitButton";

const CODE_LENGTH = 4;

/**
 * Email verification.
 *
 * Four boxes because the backend generates a four-digit code
 * (`crypto.randomInt(1000, 10000)` in `token.service.js`). If that ever becomes
 * six, this constant and the stub both change — the length is not guessed from
 * the design.
 */
const VerifyEmailStep = ({ step }) => {
  const state = useFlowState();
  const { setEmailVerified } = useFlowActions();
  const navigate = useNavigate();

  const [digits, setDigits] = useState(Array(CODE_LENGTH).fill(""));
  const [error, setError] = useState(null);
  const [resent, setResent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  // Synchronous companion to `submitting`, same reason as the completion step.
  //
  // `disabled` depends on React having re-rendered between the two presses.
  // QA recorded a same-turn double click sending two `POST /v2/auth/otp/verify`
  // on every attempt: 400/400, 400/400, then 204/400. That last pair is the
  // damaging one — the code verified, and the member was shown the second
  // request's failure on a verification that had worked.
  //
  // Each duplicate also burns one of the five attempts before the code is
  // dead, so a double-clicking member reaches the lockout in half the tries.
  const otpOperationInFlight = useRef(false);

  const code = digits.join("");
  const complete = code.length === CODE_LENGTH;

  const handleVerify = async () => {
    if (!complete || submitting) return;

    if (otpOperationInFlight.current) return;
    otpOperationInFlight.current = true;
    setSubmitting(true);
    setError(null);

    try {
      // Identified by the token minted at registration, not by the email
      // address: an OTP that could be redeemed by naming any account would be
      // a takeover primitive rather than a verification.
      await verifyEmailOtp(code);

      setEmailVerified(true);

      const next = nextStep(step.id, { ...state, emailVerified: true });
      if (next) navigate(`${ONBOARDING_BASE_PATH}/${next.path}`);

      // No reset on success: the navigate unmounts this screen.
    } catch (error) {
      setError(error?.message ?? t("onboarding.verifyEmail.failed"));
      // Released on failure so a corrected code can be submitted. Not released
      // on success: the navigate unmounts this screen.
      otpOperationInFlight.current = false;
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (resending || otpOperationInFlight.current) return;

    otpOperationInFlight.current = true;
    setError(null);
    setResending(true);
    try {
      // Authenticated and per-user rate limited server-side: the restricted
      // session may only ask for a new code, which stops the resend button
      // from becoming an open email relay.
      await resendEmailOtp();
      setResent(true);
    } catch (error) {
      if (error?.sessionExpired) {
        navigate("/login", { replace: true, state: { sessionExpired: true } });
        return;
      }
      setError(error?.message ?? t("onboarding.verifyEmail.resendFailed"));
    } finally {
      otpOperationInFlight.current = false;
      setResending(false);
    }
  };

  return (
    <Box
      component="form"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        handleVerify();
      }}
    >
      <Typography variant="h1" sx={{ mb: 1.5 }}>
        {t("onboarding.verifyEmail.heading")}
      </Typography>

      <Typography sx={{ color: onboardingTokens.mutedForeground, mb: 4 }}>
        {t("onboarding.verifyEmail.body", { email: state.email ?? "" })}
      </Typography>

      <OtpInput value={digits} onChange={setDigits} error={Boolean(error)} />

      {error && (
        <Alert severity="error" sx={{ mt: 3 }}>
          {error}
        </Alert>
      )}

      <FormSubmitButton
        variant="contained"
        fullWidth
        disabled={submitting || resending}
        disabledReason={
          !complete && !submitting && !resending
            ? t("onboarding.verifyEmail.incomplete")
            : ""
        }
        sx={{ mt: 4, py: 1.5 }}
      >
        {t("onboarding.verifyEmail.submit")}
      </FormSubmitButton>

      <Button
        type="button"
        fullWidth
        disabled={resending || submitting}
        onClick={handleResend}
        sx={{ mt: 1.5 }}
      >
        {t("onboarding.verifyEmail.resend")}
      </Button>

      {/* Confirmation is a transient snackbar rather than persistent text: it
          needs to be noticed once, not to sit on the screen implying the code
          was sent again every time the member looks at it. */}
      <Snackbar
        open={resent}
        autoHideDuration={4000}
        onClose={() => setResent(false)}
        message={t("onboarding.verifyEmail.resent")}
      />
    </Box>
  );
};

export default VerifyEmailStep;
